import type { Localized } from '@/i18n'

export interface IntegrationGap {
  id: string
  feature: Localized
  /** What exists today in Dispatch that partially covers it. */
  current: string
  /** Backend / API that must be added for the feature to be real. */
  required: string[]
  /** Nav page ids that work today and are closest to this need. */
  related?: string[]
}

/** Mirrors docs/INTEGRATION_GAPS.md — features requested for Aliens OS that the Dispatch API cannot serve yet. */
export const GAPS: Record<string, IntegrationGap> = {
  'GAP-WH-01': {
    id: 'GAP-WH-01',
    feature: { en: 'Warehouse dashboard KPIs (stock on hand, dwell time, throughput)', ar: 'مؤشرات لوحة المخزن (الرصيد، مدة البقاء، معدل الحركة)' },
    current: 'Only shipment detail inquiry and cell assignment lists; no aggregated warehouse metrics.',
    required: ['GET /api/Warehouse/Summary?branchId&date → {inStock, receivedToday, dispatchedToday, avgDwellHours, byCell[]}', 'Server-side aggregation (indexed by BranchID, StatusID, LastTouchDate)'],
    related: ['reports.details', 'operations.cells.list'],
  },
  'GAP-WH-02': {
    id: 'GAP-WH-02',
    feature: { en: 'Bagging (consolidate AWBs into sealed bags)', ar: 'التعبئة في أكياس (تجميع الشحنات في أكياس مختومة)' },
    current: 'No bag entity. Transfers support a seal number per transfer only.',
    required: ['POST /api/Bags/Add {bagNo, sealNo, toBranchId, awbs[]}', 'POST /api/Bags/Close/{BagID}', 'GET /api/Bags/GetByBagNo/{BagNo}', 'POST /api/Bags/GetPage', 'Link bags to TransferAWBs / HaulTrips'],
    related: ['branches.send', 'linehaul.send'],
  },
  'GAP-WH-03': {
    id: 'GAP-WH-03',
    feature: { en: 'Warehouse reports (aging, scan compliance, cell utilisation)', ar: 'تقارير المخزن (أعمار الشحنات، الالتزام بالمسح، استغلال الخلايا)' },
    current: 'Shipment detail inquiry can be filtered by branch/status but has no aging or scan history aggregation.',
    required: ['POST /api/ReportsAndInquiry/WarehouseAging', 'POST /api/ReportsAndInquiry/ScanCompliance', 'POST /api/ReportsAndInquiry/CellUtilisation'],
    related: ['reports.details'],
  },
  'GAP-CS-01': {
    id: 'GAP-CS-01',
    feature: { en: 'Escalations with SLA timers and owner assignment', ar: 'التصعيدات مع مؤقت SLA وتعيين المسؤول' },
    current: 'Tickets and client requests exist with statuses, but no escalation level, SLA deadline or assignee fields.',
    required: ['Add escalationLevel, assignedToUserId, slaDueAt to ContactUs tickets / requests', 'POST /api/ContactUs/Escalate {id, level, assignee, note}', 'POST /api/ContactUs/GetEscalations (filter by level, overdue)'],
    related: ['cs.tickets', 'cs.complaints'],
  },
  'GAP-CS-02': {
    id: 'GAP-CS-02',
    feature: { en: 'In-app WhatsApp conversation threads (read / reply)', ar: 'محادثات واتساب داخل النظام (قراءة ورد)' },
    current: 'Chat list + deep links (GetWhatsappChatList, GetWhatsAppFullLink); messages cannot be read or sent through the API.',
    required: ['GET /api/WhatsApp/Conversations/{chatId}/Messages', 'POST /api/WhatsApp/Conversations/{chatId}/Send', 'Webhook for inbound messages → gateway'],
    related: ['cs.shipmentSearch'],
  },
  'GAP-FIN-01': {
    id: 'GAP-FIN-01',
    feature: { en: 'Branch payment request / approval workflow', ar: 'دورة طلب واعتماد مدفوعات الفروع' },
    current: 'Branch payments can be created and posted; there is no request → approve → pay state machine.',
    required: ['POST /api/BranchPayment/Request', 'PUT /api/BranchPayment/Approve/{TransID}', 'PUT /api/BranchPayment/Reject/{TransID} {reason}', 'Request status on BranchPayment/GetPage'],
    related: ['finance.branchPayments'],
  },
  'GAP-FIN-02': {
    id: 'GAP-FIN-02',
    feature: { en: 'Dedicated collection (payout) requests from clients', ar: 'طلبات التحصيل (صرف المستحقات) من العملاء' },
    current: 'Client requests (ContactUs) have types; a payout request type can be used but carries no amount / AWB linkage.',
    required: ['POST /api/PayToCust/Requests/GetPage', 'PUT /api/PayToCust/Requests/Approve/{id} → creates PayToCust draft'],
    related: ['cs.complaints', 'finance.payToCustomer'],
  },
  'GAP-RUN-01': {
    id: 'GAP-RUN-01',
    feature: { en: 'Runner payroll / payouts (commissions paid to runners)', ar: 'مستحقات المناديب (صرف العمولات)' },
    current: 'Per-AWB delivery commission can be edited; no payout transaction or runner balance endpoint.',
    required: ['POST /api/RunnerPayouts/GetBalance/{RunnerID} {fromDate,toDate}', 'POST /api/RunnerPayouts/Add', 'PUT /api/RunnerPayouts/Post/{TransID}'],
    related: ['finance.commission', 'finance.runnerInquiry'],
  },
  'GAP-RUN-02': {
    id: 'GAP-RUN-02',
    feature: { en: 'Runner documents (license, ID, contracts) with expiry alerts', ar: 'مستندات المناديب (رخصة، بطاقة، عقود) مع تنبيهات الانتهاء' },
    current: 'Employees have profile fields only; no document storage.',
    required: ['POST /api/Employees/Documents/Upload/{EmployeeID} (multipart)', 'GET /api/Employees/Documents/{EmployeeID}', 'DELETE /api/Employees/Documents/{DocumentID}', 'Object storage for files'],
    related: ['runners.manage'],
  },
  'GAP-HR-01': {
    id: 'GAP-HR-01',
    feature: { en: 'Attendance (check-in / check-out, shifts)', ar: 'الحضور والانصراف (تسجيل الدخول والخروج والورديات)' },
    current: 'No attendance data in Dispatch.',
    required: ['POST /api/Attendance/CheckIn {employeeId, lat, lng}', 'POST /api/Attendance/CheckOut', 'POST /api/Attendance/GetPage {fromDate,toDate,branchId}', 'Shift definitions'],
    related: ['admin.employees'],
  },
  'GAP-HR-02': {
    id: 'GAP-HR-02',
    feature: { en: 'Department management (create / edit)', ar: 'إدارة الأقسام (إضافة وتعديل)' },
    current: 'Departments can be listed (Employees/HrDepartmentsList) but not created or edited.',
    required: ['POST /api/Employees/HrDepartments/Add', 'PUT /api/Employees/HrDepartments/Update'],
    related: ['admin.employees'],
  },
  'GAP-NOT-01': {
    id: 'GAP-NOT-01',
    feature: { en: 'Categorised alerts (operations / finance / customer service)', ar: 'تنبيهات مصنفة (عمليات / مالية / خدمة عملاء)' },
    current: 'Notifications endpoints return a flat list without a category field.',
    required: ['Add category + severity + entity reference to Notifications', 'GET /api/Notifications/GetLast/{n}?category=', 'Server-sent events or push (FCM already supported via UpdateTokenFCM)'],
    related: ['notifications.all'],
  },
  'GAP-NOT-02': {
    id: 'GAP-NOT-02',
    feature: { en: 'Approval requests inbox', ar: 'صندوق طلبات الاعتماد' },
    current: 'Transactions have Post/UnPost but no approval request entity.',
    required: ['GET /api/Approvals/Pending (DRS, payments, transfers awaiting approval)', 'PUT /api/Approvals/{id}/Approve', 'PUT /api/Approvals/{id}/Reject'],
    related: ['operations.transactions'],
  },
  'GAP-NOT-03': {
    id: 'GAP-NOT-03',
    feature: { en: 'Per-user notification preferences', ar: 'تفضيلات الإشعارات لكل مستخدم' },
    current: 'No preferences storage.',
    required: ['GET/PUT /api/Notifications/Preferences (channels × categories)'],
    related: ['settings.notifications'],
  },
  'GAP-INT-01': {
    id: 'GAP-INT-01',
    feature: { en: 'Payment gateway integration (card / wallet COD settlement)', ar: 'ربط بوابات الدفع (تحصيل بالبطاقة / المحافظ)' },
    current: 'No payment gateway endpoints.',
    required: ['POST /api/Payments/CreateLink {awb, amount}', 'Webhook /api/Payments/Callback', 'Reconciliation feed into CollectionReview'],
    related: ['cod.dashboard'],
  },
  'GAP-INT-02': {
    id: 'GAP-INT-02',
    feature: { en: 'WooCommerce / EasyOrders connectors', ar: 'ربط WooCommerce و EasyOrders' },
    current: 'Only Shopify stores are exposed (Shopify/GetPage).',
    required: ['Store connector entity per platform (type, credentials ref, sync status)', 'POST /api/Integrations/Stores/GetPage', 'POST /api/Integrations/Stores/Sync/{id}'],
    related: ['integrations.ecommerce'],
  },
  'GAP-INT-03': {
    id: 'GAP-INT-03',
    feature: { en: 'Outbound webhooks (status change events to clients)', ar: 'Webhooks صادرة (أحداث تغير الحالة للعملاء)' },
    current: 'No webhook subscription endpoints.',
    required: ['POST /api/Webhooks/Subscriptions {clientId, url, events[], secret}', 'GET /api/Webhooks/Deliveries?subscriptionId (delivery log + retry)'],
    related: ['settings.api'],
  },
  'GAP-INT-04': {
    id: 'GAP-INT-04',
    feature: { en: 'Integration logs (Shopify sync, external carriers, webhooks)', ar: 'سجلات التكامل (مزامنة Shopify، شركات الشحن، Webhooks)' },
    current: 'Gateway request log covers COREX traffic only; Dispatch does not expose integration job logs.',
    required: ['POST /api/Integrations/Logs/GetPage {source, fromDate, toDate, status}'],
    related: ['admin.system'],
  },
  'GAP-API-02': {
    id: 'GAP-API-02',
    feature: { en: 'Line haul trip operations (create, assign vehicle/driver, depart, arrive, track)', ar: 'تشغيل رحلات النقل (إنشاء، تعيين مركبة/سائق، مغادرة، وصول، تتبع)' },
    current: 'HaulTrips/GetPage answers HTTP 500 and HaulTrips/GetStatusList HTTP 400 on the live API (2026-09-16), so trips cannot be listed or tracked. Routes and vehicles work and are live in COREX.',
    required: ['Fix POST /api/HaulTrips/GetPage (500)', 'Fix GET /api/HaulTrips/GetStatusList (400)', 'Sample request for POST /api/HaulTrips/Add and PUT /api/HaulTrips/Depart | Arrive'],
    related: ['linehaul.routes', 'linehaul.vehicles', 'branches.send'],
  },
}
